import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';

const InfiniteScroll = ({
  children,
  onLoadMore,
  hasMore = true,
  loading = false,
  threshold = 200,
  loadingComponent = null,
}) => {
  const sentinelRef = useRef(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const onLoadMoreRef = useRef(onLoadMore);
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  });

  // Observe the sentinel; rootMargin extends the trigger zone by `threshold`px.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => setIsIntersecting(entries[0]?.isIntersecting ?? false),
      { root: null, rootMargin: `0px 0px ${threshold}px 0px`, threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  // Auto-load while the sentinel is visible and we're allowed to load.
  // Re-runs when `loading` flips false, so it keeps filling until content overflows
  // (sentinel leaves view) or hasMore === false.
  useEffect(() => {
    if (isIntersecting && hasMore && !loading) {
      onLoadMoreRef.current?.();
    }
  }, [isIntersecting, hasMore, loading]);

  return (
    <Box>
      {children}

      {loading &&
        (loadingComponent || (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress size={36} thickness={4} />
          </Box>
        ))}

      {/* Sentinel must always render (even when not loading) so the observer can
          detect the bottom of the list. */}
      <Box
        ref={sentinelRef}
        sx={{ height: '1px', width: '100%' }}
        aria-hidden="true"
      />
    </Box>
  );
};

InfiniteScroll.propTypes = {
  children: PropTypes.node.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  hasMore: PropTypes.bool,
  loading: PropTypes.bool,
  threshold: PropTypes.number,
  loadingComponent: PropTypes.node,
};

export default InfiniteScroll;